import { Icon as BlueIcon } from '@blueprintjs/core';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import ReactTooltip from 'react-tooltip';
import * as yup from 'yup';

import {
  UpdateStatusPriority,
  UpdateStatusType,
} from '@models/UpdateStatus.model';

import { useHookstate } from '@hookstate/core';

import { DateInput } from '@components/DateInput/DateInput';

import { CustomPicker } from './Components/CustomPickerWithBorder/CustomPicker';
import {
  Container,
  Title,
  Line,
  Column,
  Input,
  Divider,
  TextArea,
  IconWrapper,
  AssignButton,
  DisabledWrapper,
} from './Details.styles';
import {
  getEnabledMap,
  createDefaultDetails,
  options,
  getDateLabel,
} from './Details.utils';
import { IDetails, IDetailsFormData } from './types';

const schema = yup
  .object({
    title: yup.string().required(),
    priority: yup
      .string()
      .required()
      .oneOf([...Object.values(UpdateStatusPriority), UpdateStatusType.NEW]),
    date: yup.date().required(),
    description: yup.string().required(),
  })
  .required();
const insightsSchema = schema.shape({
  priority: yup.string().optional().nullable(),
  originalMessage: yup.string().url().required(),
});

const Details = (props: IDetails) => {
  const { mode, update, onValidChange, broadcast } = props;
  const open = useHookstate(false);
  const [enabledMap] = useState(() => getEnabledMap(mode));
  const useUpdateAsDefault = mode?.includes('editing');
  const isInsights = mode === 'editing.insights';
  const defaultWithUpdate = createDefaultDetails(update);

  const defaultValues = createDefaultDetails();

  const {
    register,
    control,
    formState: { isValid },
    watch,
  } = useForm<IDetailsFormData>({
    resolver: yupResolver(isInsights ? insightsSchema : schema),
    mode: 'onChange',
    defaultValues: useUpdateAsDefault ? defaultWithUpdate : defaultValues,
  });
  useEffect(() => {
    broadcast(watch());
    const subscription = watch(values => {
      broadcast(values);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    onValidChange(isValid);
  }, [isValid]);

  const disablePastDates = mode !== 'editing.insights';

  return (
    <Container>
      <form>
        <Line>
          <div className="DetailColumn">
            {enabledMap.get('title') && (
              <Column>
                <Title>Title</Title>
                <Input
                  type="text"
                  autoFocus
                  placeholder="Ex: Studio 3 Maintenance Shutdown"
                  {...register('title')}
                />
              </Column>
            )}

            {enabledMap.get('description') && (
              <Column>
                <Title>Description</Title>
                <Divider height="8px" />
                <TextArea
                  placeholder="Add a description"
                  {...register('description')}
                />
              </Column>
            )}
          </div>
          <Column className="priorityWrapper DetailColumn">
            {enabledMap.get('priority') && (
              <Column className="priorityWrapper">
                <Title>Priority</Title>
                <Divider height="8px" />
                <Controller
                  control={control}
                  name="priority"
                  render={({ field: { onChange, value } }) => (
                    <CustomPicker
                      options={options}
                      value={options.find(c => c.value === value)}
                      onChange={(val: any) => {
                        onChange(val.value);
                      }}
                      placeholder="Choose..."
                    />
                  )}
                />
              </Column>
            )}

            {enabledMap.get('date') && (
              <DisabledWrapper>
                <Title>{getDateLabel(mode)}</Title>
                <Divider height="8px" />
                <Controller
                  control={control}
                  name="date"
                  render={({ field: { onChange, value } }) => (
                    <DateInput
                      placeholder="Choose..."
                      height={42}
                      value={value ?? null}
                      disablePast={disablePastDates}
                      handleDateChange={newDate => {
                        let convDate: Date | null = null;
                        if (newDate) convDate = new Date(newDate);
                        onChange(convDate);
                      }}
                    />
                  )}
                />
              </DisabledWrapper>
            )}
            {enabledMap.get('assignee') && (
              <DisabledWrapper data-tip data-for="comingSoonTooltip">
                <Title className="optional">Assignee (Optional)</Title>
                <AssignButton onClick={() => open.set(true)}>
                  Choose...
                  <IconWrapper>
                    <BlueIcon icon="chevron-down" color="#8F95B2" size={18} />
                  </IconWrapper>
                </AssignButton>
              </DisabledWrapper>
            )}
            {enabledMap.get('originalMessage') && (
              <DisabledWrapper>
                <Title>Original Message</Title>
                <Input type="text" autoFocus {...register('originalMessage')} />
              </DisabledWrapper>
            )}
            <ReactTooltip
              className="tooltip"
              id="comingSoonTooltip"
              effect="solid"
              place="top"
              multiline
            >
              Coming Soon
            </ReactTooltip>
          </Column>
        </Line>

        {/*   <Line>
           <ChangeAssignee
            open={open}
            actualUser={null}
            handleUserChange={handleUserChange}
          />
        </Line>
        */}
      </form>
    </Container>
  );
};

export { Details };
